// AUMA — Dati per la fattura, obbligatori nel carrello.
// Raccoglie il codice fiscale (privati) o ragione sociale + P.IVA + SDI/PEC (aziende) e li
// salva come ATTRIBUTI DEL CARRELLO via /cart/update.js. Valida mentre si scrive e trattiene
// il bottone «Vai al check-out» finche' i dati non ci sono.
//
// **L'obbligo vero non e' qui.** Questo e' l'accompagnamento: chi salta il carrello lo ferma
// la Validation Function server-side (repo `aumadev/auma-checkout`), che vale su tutte le superfici.
// Le due cose vanno insieme — vedi il commento in testa a `snippets/auma-carrello-fiscale.liquid`.
//
// Scelte che sembrano strane e non lo sono:
// - **Niente associazione ai form del tema.** `cart-summary` e' reso due volte, pagina e
//   cassetto, quindi in pagina ci sono due `#checkout`: un `form=` punterebbe al primo che
//   capita. Tutto passa da /cart/update.js.
// - **`persist()` restituisce l'esito**, e si va al check-out solo se il salvataggio e'
//   riuscito: altrimenti l'ordine arriverebbe senza i dati che qui si dichiarano obbligatori.
// - **Si salva anche mentre si scrive** (con un ritardo): il carrello si ridisegna da solo a
//   ogni cambio di quantita', e senza questo i campi tornerebbero vuoti.
// - **Le due istanze si tengono allineate** con un evento: sono lo stesso carrello.
const KEYS = {
  fattura: 'fattura_richiesta',
  tipo: 'tipo_cliente', country: 'fiscal_country', cf: 'codice_fiscale',
  rs: 'ragione_sociale', piva: 'partita_iva', sdi: 'codice_sdi', pec: 'pec',
};
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Il sito inglese ha i suoi testi: la lingua e' quella della pagina (`<html lang>`).
const EN = (document.documentElement.lang || '').toLowerCase().startsWith('en');
const T = (it, en) => (EN ? en : it);
let UID = 0;

class AumaCarrelloFiscale extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    this.uid = 'aumaf' + (++UID);

    this.radios = [...this.querySelectorAll('[data-auma-tipo]')];
    this.groups = {
      privato: this.querySelector('[data-auma-gruppo="privato"]'),
      azienda: this.querySelector('[data-auma-gruppo="azienda"]'),
    };
    this.countryInput = this.querySelector('[data-auma-country]');
    this.esteroInput = this.querySelector('[data-auma-estero]');
    this.cf = this.querySelector('[data-auma-cf]');
    this.cfLabel = this.querySelector('[data-auma-cf-label]');
    // Dal 2026-10-24 la fattura e' facoltativa: senza la casella spuntata non si chiede niente.
    this.fatturaInput = this.querySelector('[data-auma-fattura]');
    this.dettagli = this.querySelector('[data-auma-dettagli]');
    this.pivaLabel = this.querySelector('[data-auma-piva-label]');
    this.globalErr = this.querySelector('[data-auma-err-globale]');
    this.fields = [...this.querySelectorAll('[data-auma-campo]')];

    // Nome-gruppo radio UNICO per istanza (evita interferenza tra pagina e drawer).
    this.radios.forEach((r) => { r.name = this.uid + '-tipo'; });

    // a11y: id univoci per gli errori + collegamento aria-describedby.
    this.querySelectorAll('[data-auma-err-for]').forEach((el) => {
      const key = el.getAttribute('data-auma-err-for');
      el.id = this.uid + '-err-' + key;
      const input = this.querySelector('[data-auma-' + key + ']');
      if (input) input.setAttribute('aria-describedby', el.id);
    });

    this.radios.forEach((r) => r.addEventListener('change', () => this.onTipoChange()));
    if (this.esteroInput) this.esteroInput.addEventListener('change', () => this.onEsteroChange());
    if (this.fatturaInput) this.fatturaInput.addEventListener('change', () => this.syncTipo(true));

    this.fields.forEach((input) => {
      input.addEventListener('input', () => {
        if (input.matches('[data-auma-cf], [data-auma-sdi]')) {
          const p = input.selectionStart;
          input.value = input.value.toUpperCase();
          try { input.setSelectionRange(p, p); } catch (e) {}
        }
        this.clearError(input);
        this.debouncedPersist();
      });
      input.addEventListener('change', () => { this.validateField(input); this.persist(); });
    });

    // Sincronizza le istanze pagina/drawer (stesso carrello).
    this._onSync = (e) => { if (e.detail && e.detail.uid !== this.uid) this.applyExternal(e.detail.attrs); };
    document.addEventListener('auma:fiscale-sync', this._onSync);

    this.syncTipo(false);
    this.syncEstero(false);
    bindGlobalGate();
  }

  disconnectedCallback() {
    if (this._onSync) document.removeEventListener('auma:fiscale-sync', this._onSync);
  }

  get tipo() {
    const c = this.radios.find((r) => r.checked);
    return c ? c.value : 'privato';
  }
  get isEstero() { return !!(this.esteroInput && this.esteroInput.checked); }
  get vuoleFattura() { return !this.fatturaInput || this.fatturaInput.checked; }

  onTipoChange() { this.syncTipo(true); }
  onEsteroChange() { this.syncEstero(true); }

  syncTipo(persist) {
    const tipo = this.tipo;
    this.dataset.tipo = tipo;
    if (this.dettagli) this.dettagli.hidden = !this.vuoleFattura;
    ['privato', 'azienda'].forEach((g) => {
      const grp = this.groups[g];
      if (!grp) return;
      const on = g === tipo && this.vuoleFattura;
      grp.hidden = !on;
      grp.querySelectorAll('input').forEach((i) => { i.disabled = !on; });
    });
    if (persist) this.persist();
  }

  syncEstero(persist) {
    const estero = this.isEstero;
    if (this.countryInput) this.countryInput.value = estero ? 'XX' : 'IT';
    if (this.cfLabel) this.cfLabel.textContent = estero
      ? T('Codice Fiscale / Tax ID', 'Tax ID')
      : T('Codice Fiscale', 'Italian tax code (Codice Fiscale)');
    if (this.pivaLabel) this.pivaLabel.textContent = estero
      ? T('Partita IVA / VAT number', 'VAT number')
      : T('Partita IVA', 'VAT number');
    if (this.cf) this.cf.maxLength = estero ? 40 : 16;
    if (persist) this.persist();
  }

  // ---- Validazione ----
  fieldError(input) {
    if (!input || input.disabled) return '';
    const raw = (input.value || '');
    const v = raw.replace(/\s/g, '').toUpperCase();
    const estero = this.isEstero;
    if (input.matches('[data-auma-cf]')) {
      if (!v) return estero ? T('Inserisci il tuo Tax ID.', 'Enter your tax ID.')
        : T('Inserisci il Codice Fiscale.', 'Enter your Italian tax code.');
      if (!estero && !/^[A-Z0-9]{16}$/.test(v)) {
        return T('Il Codice Fiscale deve avere 16 caratteri.', 'The Italian tax code has 16 characters.');
      }
      if (estero && v.length < 4) return T('Tax ID non valido.', 'This tax ID is not valid.');
    }
    if (input.matches('[data-auma-rs]')) {
      if (!raw.trim()) return T('Inserisci la Ragione sociale.', 'Enter the company name.');
    }
    if (input.matches('[data-auma-piva]')) {
      if (!v) return estero ? T('Inserisci la Partita IVA / VAT.', 'Enter the VAT number.')
        : T('Inserisci la Partita IVA.', 'Enter the VAT number.');
      if (!estero && !/^\d{11}$/.test(v)) return T('La Partita IVA deve avere 11 cifre.', 'An Italian VAT number has 11 digits.');
    }
    return '';
  }

  errKeyOf(input) {
    return input.matches('[data-auma-cf]') ? 'cf'
      : input.matches('[data-auma-rs]') ? 'rs'
      : input.matches('[data-auma-piva]') ? 'piva' : null;
  }

  validateField(input) {
    const msg = this.fieldError(input);
    const field = input.closest('.auma-fisc__campo');
    if (field) field.classList.toggle('is-errata', !!msg);
    input.setAttribute('aria-invalid', msg ? 'true' : 'false');
    const key = this.errKeyOf(input);
    if (key) {
      const el = this.querySelector('[data-auma-err-for="' + key + '"]');
      if (el) el.textContent = msg;
    }
    return !msg;
  }

  clearError(input) {
    const field = input.closest('.auma-fisc__campo');
    if (field) field.classList.remove('is-errata');
    input.setAttribute('aria-invalid', 'false');
    if (this.globalErr) { this.globalErr.hidden = true; this.globalErr.textContent = ''; }
  }

  validateAll() {
    if (!this.vuoleFattura) return true;
    let ok = true;
    let first = null;
    this.querySelectorAll('[data-auma-req="' + this.tipo + '"]').forEach((input) => {
      const good = this.validateField(input);
      if (!good) { ok = false; if (!first) first = input; }
    });
    if (this.tipo === 'azienda' && !this.isEstero) {
      const sdi = (this.querySelector('[data-auma-sdi]').value || '').replace(/\s/g, '').toUpperCase();
      const pec = (this.querySelector('[data-auma-pec]').value || '').trim();
      const sdiOk = /^[A-Z0-9]{6,7}$/.test(sdi);
      const pecOk = EMAIL_RE.test(pec);
      const errEl = this.querySelector('[data-auma-err-for="sdi"]');
      if (!sdiOk && !pecOk) {
        ok = false;
        if (errEl) errEl.textContent = T('Inserisci il Codice SDI (6-7 caratteri) oppure una PEC valida.',
          'Enter the SDI code (6-7 characters) or a valid PEC address.');
        if (!first) first = this.querySelector('[data-auma-sdi]');
      } else if (errEl) {
        errEl.textContent = '';
      }
    }
    if (first && first.focus) first.focus();
    return ok;
  }

  // ---- Persistenza cart attributes ----
  attributesPayload() {
    const t = this.tipo;
    const attrs = {};
    attrs[KEYS.fattura] = this.vuoleFattura ? 'si' : 'no';
    if (!this.vuoleFattura) {
      Object.values(KEYS).forEach((k) => { if (k !== KEYS.fattura) attrs[k] = ''; });
      return attrs;
    }
    attrs[KEYS.tipo] = t;
    attrs[KEYS.country] = this.countryInput ? this.countryInput.value : 'IT';
    const norm = (sel, upper) => {
      const el = this.querySelector(sel);
      let v = el ? (el.value || '').trim() : '';
      if (upper) v = v.replace(/\s/g, '').toUpperCase();
      return v;
    };
    if (t === 'azienda') {
      attrs[KEYS.cf] = '';
      attrs[KEYS.rs] = norm('[data-auma-rs]', false);
      attrs[KEYS.piva] = norm('[data-auma-piva]', true);
      attrs[KEYS.sdi] = norm('[data-auma-sdi]', true);
      attrs[KEYS.pec] = norm('[data-auma-pec]', false);
    } else {
      attrs[KEYS.cf] = norm('[data-auma-cf]', true);
      attrs[KEYS.rs] = '';
      attrs[KEYS.piva] = '';
      attrs[KEYS.sdi] = '';
      attrs[KEYS.pec] = '';
    }
    return attrs;
  }

  debouncedPersist() {
    clearTimeout(this._deb);
    this._deb = setTimeout(() => this.persist(), 400);
  }

  // Ritorna la Response (res.ok = successo) o null se rete KO. Notifica le altre istanze solo se ok.
  persist() {
    const attrs = this.attributesPayload();
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attrs }),
    })
      .then((res) => {
        if (res && res.ok) {
          document.dispatchEvent(new CustomEvent('auma:fiscale-sync', { detail: { uid: this.uid, attrs } }));
        }
        return res;
      })
      .catch(() => null);
  }

  applyExternal(attrs) {
    if (!attrs) return;
    const active = document.activeElement;
    const setVal = (sel, val) => {
      const el = this.querySelector(sel);
      if (el && el !== active && val != null) el.value = val;
    };
    if (this.fatturaInput && attrs[KEYS.fattura]) this.fatturaInput.checked = attrs[KEYS.fattura] === 'si';
    const tipo = attrs[KEYS.tipo];
    if (tipo) { const r = this.radios.find((x) => x.value === tipo); if (r) r.checked = true; }
    if (this.esteroInput) this.esteroInput.checked = !!(attrs[KEYS.country] && attrs[KEYS.country] !== 'IT');
    setVal('[data-auma-cf]', attrs[KEYS.cf]);
    setVal('[data-auma-rs]', attrs[KEYS.rs]);
    setVal('[data-auma-piva]', attrs[KEYS.piva]);
    setVal('[data-auma-sdi]', attrs[KEYS.sdi]);
    setVal('[data-auma-pec]', attrs[KEYS.pec]);
    this.syncTipo(false);
    this.syncEstero(false);
  }

  showGlobalError(msg) {
    if (!this.globalErr) return;
    this.globalErr.textContent = msg;
    this.globalErr.hidden = false;
  }

}

// ---- Blocco checkout: gate GLOBALE (una sola volta) ----
// Intercetta in fase CAPTURE a livello document sia il click sul bottone "Vai al checkout"
// sia il submit del form carrello, su qualunque superficie (pagina o drawer) e prima dei
// gestori del tema. Se i dati fiscali non sono validi, blocca; altrimenti salva e va a /checkout.
let GATE_BOUND = false;
function activeFisc() {
  const all = [...document.querySelectorAll('auma-carrello-fiscale')];
  return all.find((el) => el.offsetParent !== null) || all[0] || null;
}
function fiscalGate(e) {
  const fisc = activeFisc();
  if (!fisc) return;
  e.preventDefault();
  e.stopImmediatePropagation();
  if (e.stopPropagation) e.stopPropagation();
  if (!fisc.validateAll()) {
    fisc.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return;
  }
  if (window.__aumaFiscNav) return;
  window.__aumaFiscNav = true;
  fisc.persist().then((res) => {
    window.__aumaFiscNav = false;
    if (res && res.ok) window.location.href = '/checkout';
    else fisc.showGlobalError(T('Non è stato possibile salvare i dati (errore di rete). Riprova.',
      'We could not save your details (network error). Please try again.'));
  });
}
function bindGlobalGate() {
  if (GATE_BOUND) return;
  GATE_BOUND = true;
  document.addEventListener('click', (e) => {
    const t = e.target;
    const btn = t && t.closest ? t.closest('[name="checkout"], #checkout') : null;
    if (btn) fiscalGate(e);
  }, true);
  document.addEventListener('submit', (e) => {
    const s = e.submitter;
    if (s && (s.getAttribute('name') === 'checkout' || s.id === 'checkout')) fiscalGate(e);
  }, true);
}

if (!customElements.get('auma-carrello-fiscale')) {
  customElements.define('auma-carrello-fiscale', AumaCarrelloFiscale);
}
